/**
 * Turns a plain object into FormData the way axios did when a request set
 * `Content-Type: multipart/form-data` on a plain object, so the calls written
 * for axios keep sending what the API expects:
 *
 * - File/Blob values are appended as is, Dates as ISO strings;
 * - `null` becomes `''` (Laravel turns it back into null), `undefined` is skipped;
 * - arrays of scalars become `key[]`, nested objects and arrays of objects
 *   `key[index][field]` (Laravel's bracket format);
 * - empty arrays and objects send nothing, like axios.
 */
type Scalar = string | number | boolean | bigint

function isPlainValue(value: unknown): value is Scalar {
  return ['string', 'number', 'boolean', 'bigint'].includes(typeof value)
}

function isFlatArray(value: unknown[]): boolean {
  return value.every(
    (item) =>
      item === null ||
      item === undefined ||
      typeof item !== 'object' ||
      item instanceof Blob ||
      item instanceof Date
  )
}

function appendValue(formData: FormData, key: string, value: unknown) {
  if (value === undefined) {
    return
  }
  if (value === null) {
    formData.append(key, '')
  } else if (value instanceof Blob) {
    formData.append(key, value)
  } else if (value instanceof Date) {
    formData.append(key, value.toISOString())
  } else if (isPlainValue(value)) {
    formData.append(key, String(value))
  }
}

function build(formData: FormData, value: unknown, key: string) {
  if (Array.isArray(value)) {
    if (isFlatArray(value)) {
      value.forEach((item) => appendValue(formData, `${key}[]`, item))
    } else {
      value.forEach((item, index) => build(formData, item, `${key}[${index}]`))
    }
    return
  }

  if (
    value !== null &&
    typeof value === 'object' &&
    !(value instanceof Blob) &&
    !(value instanceof Date)
  ) {
    for (const [field, fieldValue] of Object.entries(value)) {
      build(formData, fieldValue, `${key}[${field}]`)
    }
    return
  }

  appendValue(formData, key, value)
}

export function toFormData(data: Record<string, unknown>): FormData {
  const formData = new FormData()

  for (const [key, value] of Object.entries(data)) {
    build(formData, value, key)
  }

  return formData
}
